import React from 'react'
import {
  Center,
  Divider,
  Flex,
  Heading,
  Text,
  Stack,
  Alert,
  AlertIcon,
  AlertTitle,
} from '@chakra-ui/react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { ArrowBackIcon } from '@chakra-ui/icons'
import Header from '../../../app/components/Header'
import LineChartCard from './LineChartCard'
import { useGetCompareDevicesQuery } from '../api/analysisApi'

const CompareData = () => {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  const { date_ranges, devices, parameterId, shouldCompareOneDevice } = location.state as {
    date_ranges: any
    devices: any
    parameterId: number
    shouldCompareOneDevice: boolean
  }

  const { data: { results: devicesData } = { results: [] } } = useGetCompareDevicesQuery({
    date_ranges,
    devices,
    parameter_id: parameterId,
    period: searchParams.get('period') || '24',
  })

  const groupedParameters = [
    {
      parameter_title: devicesData[0]?.parameter_title || 'Grouped',
      measurement_unit: devicesData[0]?.measurement_unit || 'Wh',
      value: devicesData.reduce((sum, d) => sum + (d.total_value ?? 0), 0),
      previous_value: 0,
      period: [],
      previous_period: [],
    },
  ]

  devicesData.forEach((device) => {
    device.datas.forEach((point) => {
      const idx = point.index
      const value = point.value ?? 0

      if (!groupedParameters[0].period[idx]) {
        groupedParameters[0].period[idx] = {
          bucket: idx,
          value: 0,
        }
      }

      groupedParameters[0].period[idx].value += value
    })
  })

  return (
    <>
      <Header>
        <Heading as="h2" fontSize="2xl" fontWeight="bold">
          <Stack direction="row">
            <ArrowBackIcon
              _hover={{ cursor: 'pointer' }}
              onClick={() => navigate(-1)}
            />
            <Text>Compare Data</Text>
          </Stack>
        </Heading>
      </Header>

      {(devicesData.length === 0 || devicesData.every(d => (d.total_value ?? 0) === 0)) ? (
        <Center w="full" mt={10} px={4}>
          <Alert
            status="info"
            bg="blue.50"
            borderRadius="md"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            w="100%"
            color="blue.800"
            py={10}
          >
            <AlertIcon boxSize="30px" mr={0} color="blue.500" />
            <AlertTitle mt={2} mb={1} fontSize="md" fontWeight="bold">
              No data available for the period
            </AlertTitle>
          </Alert>
        </Center>
      ) : (
        <Stack
          direction="column"
          alignItems="stretch"
          mt={5}
          px={4}
          spacing={6}
        >
          <Flex
            direction="row"
            boxShadow="lg"
            rounded="lg"
            borderWidth="1px"
            bgColor="white"
            w="full"
            p={4}
            gap={6}
            justifyContent="space-between"
            flexWrap={{ base: 'wrap', md: 'nowrap' }}
          >
            {devicesData.map((record, idx) => (
              <React.Fragment key={idx}>
                <Flex direction="column" flex="1" minW="0">
                  <Text fontSize="sm" fontWeight="bold" color="text.light" mb={1}>
                    Device
                  </Text>
                  <Heading
                    as="h2"
                    fontSize={{ base: 'lg', md: '2xl', lg: '3xl' }}
                  >
                    {record.title}
                  </Heading>
                  <Text fontSize={{ base: 'xs', md: 'sm', lg: 'md' }} noOfLines={1}>
                    {record?.location?.title}
                  </Text>
                  {shouldCompareOneDevice && (
                    <Text fontSize={{ base: 'xs', md: 'sm', lg: 'md' }} mt={1}>
                      {`${new Date(date_ranges[idx].from_date).toLocaleDateString()} - ${new Date(
                        date_ranges[idx].to_date
                      ).toLocaleDateString()}`}
                    </Text>
                  )}
                  <Flex p={2} direction="column" mt={4}>
                    <Text fontSize="sm" fontWeight="bold" color="text.light">Total</Text>
                    <Text fontWeight={700} fontSize="20px">
                      {record?.total_value?.toFixed(2)} {record?.measurement_unit}
                    </Text>
                  </Flex>
                </Flex>

                {idx < devicesData.length - 1 && (
                  <Center height="auto">
                    <Divider orientation="vertical" />
                  </Center>
                )}
              </React.Fragment>
            ))}
          </Flex>

          <Flex
            flexDirection="column"
            w="full"
            boxShadow="lg"
            rounded="lg"
            borderWidth="1px"
            p={5}
            pt={0}
            bgColor="white"
          >
            <LineChartCard data={devicesData} />
          </Flex>
        </Stack>
      )}
    </>
  )
}

export default CompareData
